import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import { Title, SnackbarComponent, OverflowText } from "components";
import { useEffect, useState } from "react";
import { apiCall } from "services/api";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import CloudUploadOutlinedIcon from "@mui/icons-material/CloudUploadOutlined";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import InputDatePickerComponent from "components/input-date-picker/InputDatePickerComponent";
import { formatDate } from "utils/formatDate";
import { emptyToastMessage } from "consts/common";
import storage from "services/storage";
import dayjs from "dayjs";

const maxSizeInMB = 100;
const maxSizeInBytes = maxSizeInMB * 1024 * 1024;

const docTypes = {
  MONTHLY_REPORT: {
    color: "bg-[#FEF0CB]",
    text: "Monthly Report",
    type: "MONTHLY_REPORT",
    uploadText: ".PDF for monthly report",
    allowedMime: ["application/pdf"],
    allowedExtension: [".pdf"],
  },
  KSEI_SHAREHOLDER_DATABASE: {
    color: "bg-[#CEEBFD]",
    text: "KSEI Database",
    type: "KSEI_SHAREHOLDER_DATABASE",
    uploadText: ".TXT or .CSV for KSEI Database",
    allowedMime: ["text/plain", "text/csv"],
    allowedExtension: [".csv, .txt"],
  },
  INTERNAL_INTEGRATED_DATABASE: {
    color: "bg-[#E8CAFA]",
    text: "Internal Integrated Database",
    type: "INTERNAL_INTEGRATED_DATABASE",
    uploadText: ".XLSX or .CSV for Internal Integrated Database",
    allowedMime: [
      "text/csv",
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    ],
    allowedExtension: [".csv, .xlsx"],
  },
};

const docTypeSwitch = (type) => {
  return docTypes[type];
};

function formatDateToYearMonth(dateStr) {
  if (dateStr.length === 7) {
    return dateStr;
  }

  return dateStr.slice(0, 7);
}

const getDocOptions = () => {
  let types = [];
  for (const type in docTypes) {
    const element = docTypes[type];
    types.push({
      label: element.text,
      id: type,
      ...element,
    });
  }

  return types;
};

const formatFileSize = (sizeInBytes) => {
  const units = ["Bytes", "KB", "MB", "GB", "TB"];
  let size = sizeInBytes;
  let unitIndex = 0;

  while (size >= 1024 && unitIndex < units.length - 1) {
    size /= 1024;
    unitIndex++;
  }

  return `${size.toFixed(2)} ${units[unitIndex]}`;
};

const UploadPage = () => {
  const [openAutoComplete, setOpenAutoComplete] = useState(false);
  const [options, setOptions] = useState([]);
  const [loadingAutoComplete, setLoadingAutoComplete] = useState(false);

  const [uploadHistory, setUploadHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);

  const [selectedCompany, setSelectedCompany] = useState();
  const [selectedCompanyChange, setSelectedCompanyChange] = useState(0);

  const [selectedType, setSelectedType] = useState();

  const [selectedFile, setSelectedFile] = useState();
  const [fileHovered, setFileHovered] = useState(false);

  const [periode, setPeriode] = useState();
  const [periodeEnd, setPeriodeEnd] = useState();

  const [message, setMessage] = useState(emptyToastMessage);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [today] = useState(dayjs());

  const handleOpenAutoComplete = (props = { notFromClick: false }) => {
    if (!props.notFromClick) {
      setOpenAutoComplete(true);
    }

    if (options.length === 0) {
      (async () => {
        setLoadingAutoComplete(true);
        const response = await apiCall({
          url: `/v1/company`,
          queryParams: {
            limit: 50,
            sort_by: "name",
            sort_order: "asc",
            skip_pagination: true,
          },
          method: "GET",
        });
        const companies = response?.data?.companies || [];
        const formattedCompanies = companies.map((company) => {
          return {
            label: company?.name,
            id: company?.company_id,
            logoUrl: company?.logo_url,
          };
        });
        setOptions(formattedCompanies);
        if (!selectedCompany && formattedCompanies.length > 0) {
          setSelectedCompany(formattedCompanies[0]);
          setSelectedCompanyChange(selectedCompanyChange + 1);
          getUploadHistory(formattedCompanies[0]);
        }
        setLoadingAutoComplete(false);
      })();
    }
  };

  useEffect(() => {
    handleOpenAutoComplete(true);
    setOpenAutoComplete(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleCloseAutoComplete = () => {
    setOpenAutoComplete(false);
  };

  const handleChangeAutoComplete = (_, val) => {
    setSelectedCompany(val);
    if (val) {
      getUploadHistory(val);
    } else {
      setUploadHistory([]);
    }
  };

  const getUploadHistory = async (selectedCompany) => {
    setLoadingHistory(true);
    const response = await apiCall({
      url: `/v1/company/${selectedCompany?.id}/upload/history`,
      queryParams: {
        limit: 4,
      },
      method: "GET",
    });
    const history = response?.data || [];
    setUploadHistory(history);
    setLoadingHistory(false);
  };

  const handleChangeDocsType = (_, val) => {
    if (val?.type) {
      setSelectedType(val.type);
    } else {
      setSelectedType(null);
    }
    discardFile();
    // Reset dates when changing document type
    setPeriode(null);
    setPeriodeEnd(null);
  };

  const fileSetter = (file) => {
    const allowedFileTypes = docTypes[selectedType]?.allowedMime || [];

    if (!allowedFileTypes.includes(file.type)) {
      setMessage({
        title: "Failed",
        desc: "Incorrect document format.",
        type: "error",
      });
      return;
    }

    if (file.size > maxSizeInBytes) {
      setMessage({
        title: "Failed",
        desc: `Document exceeds size limit.`,
        type: "error",
      });
      return;
    }

    setSelectedFile(file);
  };

  const handleFileChange = (event) => {
    const selectedFiles = event.target.files;
    if (selectedFiles && selectedFiles.length > 0) {
      const newFiles = Array.from(selectedFiles);
      fileSetter(newFiles[0]);
    }
  };

  const discardFile = () => {
    setSelectedFile(null);
  };

  const handleDragOverFile = (event) => {
    event.preventDefault();
    setFileHovered(true);
  };

  const handleDragEndFile = (event) => {
    event.preventDefault();
    setFileHovered(false);
  };

  const handleDropFile = (event) => {
    event.preventDefault();
    setFileHovered(false);
    const droppedFiles = event.dataTransfer.files;

    if (droppedFiles.length > 0) {
      const newFiles = Array.from(droppedFiles);
      fileSetter(newFiles[0]);
    }
  };

  const handleOnChangeDatePicker = (payload) => {
    setPeriode(formatDate.toAPIDate(payload));
  };

  const handleOnChangeDatePickerEnd = (payload) => {
    setPeriodeEnd(formatDate.toAPIDate(payload));
  };

  const onCloseSnackbar = () => {
    setMessage(emptyToastMessage);
  };

  const onSubmit = async () => {
    if (!selectedCompany || !selectedFile || !periode || !selectedType) {
      setMessage({
        title: "Failed",
        desc: "Please select a client, file, period, and document type.",
        type: "error",
      });
      return;
    }

    // Validate date range if both dates are selected for Internal Integrated Database
    if (selectedType === 'INTERNAL_INTEGRATED_DATABASE' && periode && periodeEnd) {
      const startDate = new Date(periode);
      const endDate = new Date(periodeEnd);
      if (endDate < startDate) {
        setMessage({
          title: "Failed",
          desc: "End date must be after or equal to start date.",
          type: "error",
        });
        return;
      }
    }

    setIsSubmitting(true);
    const formData = new FormData();
    formData.append("file", selectedFile);
    formData.append("type", selectedType);

    // Handle date formatting based on document type
    if (selectedType === 'INTERNAL_INTEGRATED_DATABASE' && periode && periodeEnd) {
      // For Internal Integrated Database with date range
      formData.append("period", periode);
      formData.append("period_end", periodeEnd);
    } else {
      // For other document types or single date
      let formattedPeriod = periode;
      if (selectedType === "MONTHLY_REPORT") {
        formattedPeriod = formatDateToYearMonth(formattedPeriod);
      }
      formData.append("period", formattedPeriod);
    }

    try {
      const resp = await apiCall({
        url: `/v1/company/${selectedCompany.id}/upload`,
        method: "POST",
        data: formData,
      });

      if (resp?.info?.title === "success") {
        setMessage({
          title: "Success",
          desc: "Document successfully uploaded.",
          type: "success",
        });

        getUploadHistory(selectedCompany);
      } else {
        throw resp;
      }
    } catch (error) {
      setMessage({
        title: "Failed",
        desc: `Error during file upload: ${error}`,
        type: "error",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className='upload-page grid grid-cols-2 gap-4'>
      <div className='bg-white shadow-sm rounded-lg grid gap-5 p-6'>
        <div className='flex flex-col gap-5'>
          <Title title='Client' titleClass='text-lg font-bold' />
          {(selectedCompany || selectedCompanyChange > 0) && (
            <Autocomplete
              disablePortal
              options={options}
              open={openAutoComplete}
              onClose={handleCloseAutoComplete}
              loading={loadingAutoComplete}
              onOpen={handleOpenAutoComplete}
              onChange={handleChangeAutoComplete}
              defaultValue={selectedCompany?.label}
              renderInput={(params) => {
                return (
                  <TextField
                    {...params}
                    placeholder='Select client'
                    sx={{ maxWidth: 594 }}
                  />
                );
              }}
              renderOption={(props, option) => (
                <li {...props} key={option.id}>
                  <span>{option.label}</span>
                </li>
              )}
            />
          )}

          {selectedCompany && (
            <div className='shadow-sm w-full rounded-lg p-4 capitalize flex gap-5'>
              {selectedCompany?.logoUrl && (
                <img
                  src={`${selectedCompany?.logoUrl}`}
                  alt={selectedCompany?.label}
                  className='max-w-[64px] max-h-[64px]'
                />
              )}
              <OverflowText
                text={selectedCompany?.label}
                textClassName='text-sm font-bold'
                maxChar={70}
              />
            </div>
          )}

          {selectedCompany && uploadHistory.length !== 0 && (
            <Title title='Upload History' titleClass='text-base font-bold' />
          )}
          <div className='grid gap-4'>
            {loadingHistory && "Loading.."}
            {!loadingHistory && (
              <>
                {!selectedCompany && (
                  <a href='/client-management'>
                    <img src='/images/no-client-yet.svg' alt='no client' />
                  </a>
                )}

                {selectedCompany && uploadHistory.length === 0 && (
                  <img src='/images/no-uploads-yet.svg' alt='no upload' />
                )}

                {!loadingHistory &&
                  uploadHistory.map((history, i) => (
                    <div key={i} className='flex gap-5 border-b pb-4'>
                      <div className='flex items-center justify-center'>
                        <DescriptionOutlinedIcon
                          className='bg-blue-100 rounded-full text-blue-500 p-2'
                          fontSize='large'
                        />
                      </div>
                      <div className='flex flex-col gap-3'>
                        <OverflowText
                          text={history.name}
                          textClassName='font-bold text-sm'
                          maxChar={60}
                        />
                        <div className='flex text-xs gap-1'>
                          <p className='text-[#5F737F]'>
                            Last uploaded on {`${history.uploaded_date}`}
                          </p>
                          <p>-</p>
                          <p
                            className={`${
                              docTypeSwitch(history.type).color
                            } py-px px-2.5 rounded`}
                          >{`${docTypeSwitch(history.type).text}`}</p>
                        </div>
                      </div>
                    </div>
                  ))}
              </>
            )}
          </div>
        </div>
      </div>

      <div className='bg-white shadow-sm rounded-lg grid gap-5 p-6'>
        <div className='flex flex-col gap-5'>
          <Title title='Upload File' titleClass='text-lg font-bold' />
          <div className='flex gap-4'>
            <Autocomplete
              className='flex-grow'
              disablePortal
              options={getDocOptions()}
              renderInput={(params) => (
                <TextField {...params} placeholder='Select document type' />
              )}
              onChange={handleChangeDocsType}
            />
            <div className="flex gap-2">
              <InputDatePickerComponent
                onChange={handleOnChangeDatePicker}
                value={periode ? dayjs(periode) : null}
                maxDate={today}
                placeholder={selectedType === 'INTERNAL_INTEGRATED_DATABASE' ? "Start Date" : "Select Date"}
              />
              {selectedType === 'INTERNAL_INTEGRATED_DATABASE' && (
                <InputDatePickerComponent
                  onChange={handleOnChangeDatePickerEnd}
                  value={periodeEnd ? dayjs(periodeEnd) : null}
                  maxDate={today}
                  placeholder="End Date"
                />
              )}
            </div>
          </div>
          <div
            className={`h-[407px] w-full border border-dashed ${
              fileHovered && "border-red-500 border-2"
            } bg-[#556FB50D] flex items-center rounded-lg`}
            onDrop={handleDropFile}
            onDragOver={handleDragOverFile}
            onDragLeave={handleDragEndFile}
          >
            <div className='flex flex-col mx-auto gap-2'>
              {!selectedFile && (
                <>
                  <CloudUploadOutlinedIcon
                    className='text-gray-400 mx-auto'
                    style={{
                      width: 64,
                      height: 64,
                    }}
                  />
                  <p className='text-base font-bold text-center'>
                    Drag & drop files or{" "}
                    <input
                      type='file'
                      hidden
                      id='doc-file'
                      accept={docTypes[selectedType]?.allowedExtension || ""}
                      onClick={(e) => {
                        if (!selectedType) {
                          setMessage({
                            title: "Failed",
                            desc: `Please select the document type first`,
                            type: "error",
                          });
                          e.preventDefault();
                        }
                      }}
                      onChange={handleFileChange}
                    />
                    <label
                      htmlFor='doc-file'
                      className='text-blue-400 underline cursor-pointer'
                    >
                      Browse
                    </label>
                  </p>
                  <p className='text-xs text-[#5F737F] text-center'>
                    {`Max. file size ${maxSizeInMB} MB ${
                      docTypes[selectedType]
                        ? `(${docTypes[selectedType].uploadText})`
                        : ""
                    }`}
                  </p>
                </>
              )}

              {selectedFile && (
                <>
                  <p className='text-base font-bold text-center'>
                    {`${selectedFile.name}`}
                  </p>
                  <p className='text-xs text-[#5F737F] text-center'>
                    {`${formatFileSize(selectedFile.size)}`}
                  </p>
                  <p
                    className='text-xs text-red-700 font-bold text-center cursor-pointer'
                    onClick={discardFile}
                  >
                    Delete
                  </p>
                </>
              )}
            </div>
          </div>
          <Button
            className={`${
              storage.getItem("role")?.includes("ADMIN") && !isSubmitting
                ? "!bg-[#556FB5]"
                : "!bg-[#B5BFC4]"
            }`}
            disabled={!storage.getItem("role")?.includes("ADMIN") || isSubmitting}
            variant='contained'
            onClick={onSubmit}
            startIcon={isSubmitting ? <CircularProgress size={20} color="inherit" /> : null}
          >
            {isSubmitting ? 'Uploading...' : 'Submit'}
          </Button>
        </div>
      </div>

      {message.title && (
        <SnackbarComponent
          labelTitle={message.title}
          labelMessage={message.desc}
          type={message.type}
          onClose={onCloseSnackbar}
          duration={3000}
        />
      )}
    </div>
  );
};

export default UploadPage;
